/**
 * Données autour des évals d'un élève : SKILLCARDS candidates (les siennes +
 * celles des packs de son année), maîtrise par carte (« à réviser ») et
 * matières proposées. Réutilise les requêtes déjà en cache ailleurs (mêmes
 * clés React Query que le tableau de bord).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SUBJECTS } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { useStudentSkillcards } from '@/lib/cards/student-skillcards';
import { useCurriculumTimeline } from '@/lib/curriculum/api';
import { buildCardCandidates } from './evaluation-cards-picker';

export function useEvaluationContext(userId: string) {
  const { data: profile } = useQuery({
    // Même clé et même sélection que le tableau de bord élève (cache partagé).
    queryKey: ['student-profile', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_profiles')
        .select('xp, streak_days, level, subjects')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const level = profile?.level ?? null;
  const rawSubjects = profile?.subjects;
  const subjects = useMemo(() => (rawSubjects ?? []) as string[], [rawSubjects]);
  const { data: skillcards = [] } = useStudentSkillcards(userId);
  const { data: timeline = [] } = useCurriculumTimeline(level, null, !!level);
  // Profs liés (lien actif) : ils voient ses notes et évals (0322) — on l'affiche.
  const { data: tutorNames = [] } = useQuery({
    queryKey: ['evaluation-shared-tutors', userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from('tutor_links')
        .select('tutor:profiles!tutor_links_tutor_id_fkey(display_name:visible_name)')
        .eq('student_id', userId)
        .eq('status', 'active');
      if (error) throw error;
      return ((data ?? []) as unknown as { tutor: { display_name: string | null } | null }[])
        .map((r) => r.tutor?.display_name?.trim() || 'ton prof')
        .filter(Boolean);
    },
  });

  return useMemo(() => {
    const packs = timeline.filter((p) => subjects.length === 0 || subjects.includes(p.subject));
    const candidates = buildCardCandidates(
      skillcards.map((c) => ({ id: c.id, title: c.title })),
      packs,
    );
    const masteryById = new Map(
      skillcards.filter((c) => c.usage_count > 0).map((c) => [c.id, c.mastery] as const),
    );
    const subjectSuggestions = SUBJECTS.filter((s) => subjects.includes(s.value)).map(
      (s) => s.label,
    );
    return { candidates, masteryById, subjectSuggestions, tutorNames };
  }, [timeline, subjects, skillcards, tutorNames]);
}
